import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Close as CloseIcon, MonetizationOn as MoneyIcon } from '@mui/icons-material';
import { bookingServiceV2 } from '@/services/bookingService.v2';
import type { BookingV2 } from '@/types';

interface RefundDialogProps {
  open: boolean;
  onClose: () => void;
  booking: BookingV2 | null;
  onSuccess: () => void;
}

export const RefundDialog: React.FC<RefundDialogProps> = ({
  open,
  onClose,
  booking,
  onSuccess
}) => {
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [refundReason, setRefundReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [refundMethod, setRefundMethod] = useState('paystack');
  const [transitionToRefundedState, setTransitionToRefundedState] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate available refund amount
  const totalPaid = booking ? parseFloat(booking.totalAmount) || 0 : 0;
  const alreadyRefunded = 0; // TODO: Get from booking.totalRefunded if available
  const maxRefundable = totalPaid - alreadyRefunded;

  const handleRefundAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and decimals
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setRefundAmount(value);
    }
  };

  const handleSubmit = async () => {
    const amount = parseFloat(refundAmount);

    // Validation
    if (!refundAmount || amount <= 0) {
      setError('Please enter a valid refund amount greater than 0');
      return;
    }

    if (amount > maxRefundable) {
      setError(`Refund amount cannot exceed ₦${maxRefundable.toLocaleString()}`);
      return;
    }

    if (!refundReason.trim()) {
      setError('Please provide a reason for the refund');
      return;
    }

    if (!booking?.id) {
      setError('Invalid booking');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      await bookingServiceV2.issueRefund(booking.id, {
        refundAmount: amount,
        refundReason: refundReason.trim(),
        adminNotes: adminNotes.trim() || undefined,
        refundMethod,
        transitionToRefundedState
      });

      onSuccess();
      handleClose();

    } catch (err: any) {
      setError(err.message || 'Failed to process refund');
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setRefundAmount('');
    setRefundReason('');
    setAdminNotes('');
    setRefundMethod('paystack');
    setTransitionToRefundedState(false);
    setError(null);
    onClose();
  };

  const isFullRefund = parseFloat(refundAmount) === maxRefundable;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoneyIcon color="primary" />
            <Typography variant="h6">Issue Refund</Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Booking info */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Booking:</strong> {booking?.bookingNumber}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Total Paid:</strong> ₦{totalPaid.toLocaleString()}
          </Typography>
          {alreadyRefunded > 0 && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Already Refunded:</strong> ₦{alreadyRefunded.toLocaleString()}
            </Typography>
          )}
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
            <strong>Maximum Refundable:</strong> ₦{maxRefundable.toLocaleString()}
          </Typography>
        </Alert>

        {/* Refund amount */}
        <TextField
          label="Refund Amount"
          required
          fullWidth
          value={refundAmount}
          onChange={handleRefundAmountChange}
          placeholder="0.00"
          InputProps={{
            startAdornment: <InputAdornment position="start">₦</InputAdornment>,
          }}
          helperText={`Maximum: ₦${maxRefundable.toLocaleString()}`}
          sx={{ mb: 2 }}
        />

        {/* Quick amount buttons */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setRefundAmount((maxRefundable * 0.25).toString())}
          >
            25%
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setRefundAmount((maxRefundable * 0.5).toString())}
          >
            50%
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setRefundAmount((maxRefundable * 0.75).toString())}
          >
            75%
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setRefundAmount(maxRefundable.toString())}
          >
            Full Refund
          </Button>
        </Box>

        {/* Refund reason */}
        <TextField
          label="Reason for Refund"
          required
          fullWidth
          multiline
          rows={3}
          value={refundReason}
          onChange={(e) => setRefundReason(e.target.value)}
          placeholder="e.g., Vehicle AC malfunction - customer complaint"
          sx={{ mb: 2 }}
        />

        {/* Admin notes */}
        <TextField
          label="Admin Notes (Internal)"
          fullWidth
          multiline
          rows={2}
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          placeholder="Internal notes for record keeping..."
          helperText="Optional - Only visible to admins"
          sx={{ mb: 2 }}
        />

        {/* Refund method */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Refund Method</InputLabel>
          <Select
            value={refundMethod}
            label="Refund Method"
            onChange={(e) => setRefundMethod(e.target.value)}
          >
            <MenuItem value="paystack">Paystack (Automatic)</MenuItem>
            <MenuItem value="wallet">User Wallet</MenuItem>
            <MenuItem value="bank_transfer">Bank Transfer (Manual)</MenuItem>
            <MenuItem value="manual">Cash/Other</MenuItem>
          </Select>
        </FormControl>

        {/* Transition to refunded state */}
        {isFullRefund && (
          <FormControlLabel
            control={
              <Checkbox
                checked={transitionToRefundedState}
                onChange={(e) => setTransitionToRefundedState(e.target.checked)}
              />
            }
            label="Mark booking as 'Refunded' (full refund only)"
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={processing}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={processing || !refundAmount || !refundReason.trim()}
        >
          {processing ? 'Processing...' : 'Issue Refund'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
